import React, { useContext } from 'react'
import { AdCreative, Permissions } from '@marketing-milk/interfaces'
import { sessionContext, useGetBusiness } from '@marketing-milk/frontend'
import { MobilePreview } from 'pages/business/creatives/creative-wizard/ad-preview/views/mobile-preview/MobilePreview'
import { isLocallyRenderable, LocalAdPreview } from '../local'

export interface CreativePreviewProps {
  /** Source of the local fallback render (copy, image/video, headline, CTA). */
  creative: AdCreative
  /**
   * Caller-extracted FB iframe src (`getIFrameSource(html)`). The sprint-packet
   * modal passes its fresh-gated source; the others pass the raw extraction. An
   * empty string means no live preview is available.
   */
  iframeSource: string
  /** True while the initial preview fetch is in flight (drives the "unavailable" decision). */
  loading: boolean
  /** True while a forced refresh is in flight (drives the "unavailable" decision). */
  refreshing: boolean
  /** Banner Retry handler — modal passes `refreshPreview`, others pass `retrievePreview`. */
  onRetry: () => void
  /** Sizing passthroughs forwarded to `MobilePreview`. */
  width?: number | string
  height?: number | string
  frameless?: boolean
  fluidWidth?: boolean
}

const bannerColors = {
  text: '#65676b',
  border: '#dadde1',
  buttonInk: '#050505',
  link: '#1877f2',
}

/**
 * Universal, controlled creative-preview surface: the live Facebook mobile-feed
 * iframe layered over the network-free `LocalAdPreview` base, plus an inline
 * "Live Facebook preview unavailable" banner that replaces the hook's error toast
 * wherever the live preview can't be shown.
 *
 * Controlled by design: each consumer already owns a `useFetchCreativePreview`
 * hook and any approval gating, so this component takes the resolved preview state
 * as props and owns only the viewport, the local fallback, the unavailable banner,
 * and the (internal-only) authorizer link. Generalized from the sprint-packet
 * `CreativePreviewModal` so the same degrade-gracefully behavior applies on the
 * creatives page, campaign summary, and campaign ad-set surfaces.
 */
export function CreativePreview({
  creative,
  iframeSource,
  loading,
  refreshing,
  onRetry,
  width,
  height,
  frameless,
  fluidWidth,
}: CreativePreviewProps) {
  const business = useGetBusiness()
  const { user } = useContext(sessionContext)

  // The live FB fetch has settled with no usable preview (down / token expired /
  // timed out). The local fallback is the only thing on screen.
  const fbUnavailable = !iframeSource && !loading && !refreshing
  const localRenderable = isLocallyRenderable(creative.format)

  // The local, Facebook-independent base layer paints instantly and stays visible
  // whenever the live FB iframe is unavailable. For carousel/dynamic we only have a
  // placeholder, so we withhold it while FB is still loading (MobilePreview shows
  // its spinner) and show it once FB has settled unavailable — never masking the
  // spinner with a non-authoritative placeholder.
  const fallback =
    localRenderable || fbUnavailable ? (
      <LocalAdPreview
        creative={creative}
        pageName={business.businessName}
        pageImageURL={business.fbPageImgURL}
      />
    ) : undefined

  // Every observed preview failure has been a Facebook token/authorization
  // problem, so we surface a link to the admin Authorizers page — but only for
  // internal staff (external sprint-packet viewers never see it).
  const showAuthorizerLink = !!user?.permissions.includes(Permissions.AccessAdminApp)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        minWidth: 0,
      }}
    >
      <MobilePreview
        iframeSource={iframeSource}
        width={width}
        height={height}
        frameless={frameless}
        fluidWidth={fluidWidth}
        fallback={fallback}
      />
      {fbUnavailable && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            color: bannerColors.text,
            fontSize: 13,
            lineHeight: 1.4,
            textAlign: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>Live Facebook preview unavailable — showing local preview.</span>
            <button
              type="button"
              onClick={onRetry}
              style={{
                border: `1px solid ${bannerColors.border}`,
                background: 'transparent',
                color: bannerColors.buttonInk,
                borderRadius: 6,
                padding: '4px 12px',
                fontSize: 13,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Retry
            </button>
          </div>
          {showAuthorizerLink && (
            <a
              href={`${process.env.REACT_APP_ADMIN}/business/authorizers/list`}
              target="_blank"
              rel="noreferrer"
              style={{ color: bannerColors.link, fontSize: 12 }}
            >
              The Facebook authorization may need attention — check authorizers ↗
            </a>
          )}
        </div>
      )}
    </div>
  )
}
